import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import axios from "axios";
import * as React from "react";
import { Modal } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";

import { useGlobalState } from "../../../behavioral";
import { PublicPlanCard } from "../../../components/pricing-components";
import { getLegacyPackageContent } from "../../../components/pricing-components/pricing";
import {
  ConfirmModal,
  IPaymentMethodUpdateResult,
  IPackage,
  IUser,
  IPublicPlan,
  ISubscriptionInvoice,
  ISubscriptionPaymentMethod,
  ISubscriptionUpgradePreview,
} from "../../../libs";
import { adminService, paymentService, userService } from "../../../services";
import "../../../components/pricing-components/pricing.scss";
import * as Styled from "./MyPlan.styled";
import { PaymentMethodForm } from "./PaymentMethodForm";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY ?? "");

const stripeAppearance = {
  theme: "night" as const,
  variables: {
    fontFamily: "Open Sans, system-ui, sans-serif",
    borderRadius: "8px",
    colorBackground: "#1e2025",
    colorText: "#ffffff",
    colorPrimary: "#b7f84d",
  },
};

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount / 100);

const formatBillingAddress = (paymentMethod: ISubscriptionPaymentMethod | null) => {
  const address = paymentMethod?.billingDetails?.address;
  if (!address) return "No billing address is available.";
  const locality = [address.city, address.state, address.postalCode].filter(Boolean).join(", ");
  return (
    [address.line1, address.line2, locality, address.country].filter(Boolean).join(" · ") ||
    "No billing address is available."
  );
};

const isExpiredCard = (paymentMethod: ISubscriptionPaymentMethod | null) => {
  if (!paymentMethod?.card) return false;
  const now = new Date();
  return (
    paymentMethod.card.expYear < now.getFullYear() ||
    (paymentMethod.card.expYear === now.getFullYear() &&
      paymentMethod.card.expMonth < now.getMonth() + 1)
  );
};

const formatPaymentMethodType = (type: string) =>
  type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const formatBillingDate = (timestamp: number) =>
  new Intl.DateTimeFormat("en-US").format(new Date(timestamp * 1000));

const errorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return (
      error.response?.data?.error?.message ||
      error.response?.data?.message ||
      "Unable to update your plan."
    );
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof error.message === "string"
  ) {
    return error.message;
  }
  return error instanceof Error ? error.message : "Unable to update your plan.";
};

export const MyPlan: React.FC = () => {
  const { state, dispatch, actions } = useGlobalState();
  const [currentPackage, setCurrentPackage] = React.useState<IPackage | null>(null);
  const [plans, setPlans] = React.useState<IPublicPlan[]>([]);
  const [paymentMethod, setPaymentMethod] = React.useState<ISubscriptionPaymentMethod | null>(null);
  const [paymentMethodSource, setPaymentMethodSource] = React.useState<
    "DEFAULT" | "LAST_USED" | "ATTACHED" | null
  >(null);
  const [paymentMethodCount, setPaymentMethodCount] = React.useState(0);
  const [invoices, setInvoices] = React.useState<ISubscriptionInvoice[]>([]);
  const [paymentClientSecret, setPaymentClientSecret] = React.useState<string>();
  const [setupClientSecret, setSetupClientSecret] = React.useState<string>();
  const [nextBillingAt, setNextBillingAt] = React.useState<number | null>(null);
  const [cancelAt, setCancelAt] = React.useState<number | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = React.useState(false);
  const [showInvoices, setShowInvoices] = React.useState(false);
  const [cancelling, setCancelling] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [openingPaymentUpdate, setOpeningPaymentUpdate] = React.useState(false);
  const [authenticatingPayment, setAuthenticatingPayment] = React.useState(false);
  const [upgradingPackageId, setUpgradingPackageId] = React.useState<string>();
  const [billingMode, setBillingMode] = React.useState<"STRIPE" | "MANUAL">("STRIPE");
  const [manualBilling, setManualBilling] = React.useState<NonNullable<
    NonNullable<IUser["subscriptionInfo"]>["manualBilling"]
  > | null>(null);
  const [pendingManualBilling, setPendingManualBilling] = React.useState<NonNullable<
    NonNullable<IUser["subscriptionInfo"]>["pendingManualBilling"]
  > | null>(null);
  const [manualAccessStatus, setManualAccessStatus] = React.useState<
    "SCHEDULED" | "ACTIVE" | "GRACE" | "EXPIRED"
  >("ACTIVE");

  const refresh = React.useCallback(async () => {
    const [subscription, publicPlans, currentUser] = await Promise.all([
      paymentService.getMySubscription(),
      adminService.getPublicPlans(),
      userService.getCurrentUser(),
    ]);
    setCurrentPackage(subscription.package);
    setPaymentMethod(subscription.paymentMethod || null);
    setPaymentMethodSource(subscription.paymentMethodSource || null);
    setPaymentMethodCount(subscription.paymentMethodCount || 0);
    setInvoices(subscription.invoices || []);
    setPaymentClientSecret(subscription.clientSecret);
    setNextBillingAt(subscription.nextBillingAt);
    setCancelAt(subscription.cancelAt);
    setCancelAtPeriodEnd(subscription.cancelAtPeriodEnd);
    setBillingMode(subscription.billingMode || "STRIPE");
    setManualBilling(subscription.manualBilling || null);
    setPendingManualBilling(subscription.subscriptionInfo?.pendingManualBilling || null);
    setManualAccessStatus(subscription.manualAccessStatus || "ACTIVE");
    setPlans(
      publicPlans.configured
        ? publicPlans.plans
        : publicPlans.plans.map((plan) => ({
            ...plan,
            package: getLegacyPackageContent(plan.package),
          }))
    );
    dispatch({
      type: actions.SET_USER,
      payload: {
        ...currentUser,
        subscriptionInfo: subscription.subscriptionInfo || currentUser.subscriptionInfo,
      },
    });
    return subscription;
  }, [actions.SET_USER, dispatch]);

  const openPaymentMethodUpdate = async () => {
    setOpeningPaymentUpdate(true);
    try {
      const setup = await paymentService.createPaymentMethodSetup();
      setSetupClientSecret(setup.clientSecret);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setOpeningPaymentUpdate(false);
    }
  };

  const finishPaymentMethodUpdate = async (result: IPaymentMethodUpdateResult) => {
    setSetupClientSecret(undefined);
    await refresh().catch((error) => toast.error(errorMessage(error)));

    if (result.retryAttempted && result.paymentStatus === "PAID") {
      toast.success("Your card was updated and the outstanding invoice was paid.");
    } else if (result.retryError || result.paymentStatus === "PAYMENT_FAILED") {
      toast.warning(
        "Your card was saved, but the outstanding payment is still unresolved. Try another card or contact support."
      );
    } else {
      toast.success("Your payment method and billing address were updated.");
    }
  };

  const completePaymentAuthentication = async () => {
    if (!paymentClientSecret) return;
    setAuthenticatingPayment(true);
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe could not be loaded.");
      const confirmation = await stripe.confirmPayment({
        clientSecret: paymentClientSecret,
        confirmParams: { return_url: window.location.href },
        redirect: "if_required",
      });
      if (confirmation.error) throw confirmation.error;
      await refresh();
      toast.success("Your bank authentication was completed.");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setAuthenticatingPayment(false);
    }
  };

  const scheduleCancellation = async () => {
    setCancelling(true);
    try {
      const result = await paymentService.cancelMySubscription();
      setCancelAtPeriodEnd(result.cancelAtPeriodEnd);
      setCancelAt(result.cancelAt);
      setNextBillingAt(result.nextBillingAt);
      await refresh();
      toast.success(`Your subscription will end on ${formatBillingDate(result.cancelAt)}.`);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCancelling(false);
    }
  };

  const showCancellationConfirmation = () => {
    const endDate = cancelAt || nextBillingAt;
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="Cancel subscription?"
          description={[
            {
              text: `Your ${currentPackage?.name || "current"} plan will remain active until ${
                endDate ? formatBillingDate(endDate) : "the end of the current billing period"
              }. You will not be charged for another renewal.`,
            },
          ]}
          danger
          noText="Keep subscription"
          yesText="Cancel subscription"
          onNo={onClose}
          onYes={() => {
            onClose();
            scheduleCancellation();
          }}
        />
      ),
      closeOnClickOutside: false,
      closeOnEscape: true,
    });
  };

  const reactivateSubscription = async (onSuccess?: () => void) => {
    setCancelling(true);
    try {
      const result = await paymentService.reactivateMySubscription();
      setCancelAtPeriodEnd(false);
      setCancelAt(null);
      setNextBillingAt(result.nextBillingAt);
      await refresh();
      toast.success(
        `Your ${currentPackage?.name || "subscription"} plan is active and will renew on ${formatBillingDate(
          result.nextBillingAt
        )}.`
      );
      onSuccess?.();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setCancelling(false);
    }
  };

  const showReactivationConfirmation = () => {
    const renewalDate = nextBillingAt || cancelAt;
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="Reactivate subscription?"
          description={[
            {
              text: `Your ${currentPackage?.name || "current"} plan will continue and renew on ${
                renewalDate ? formatBillingDate(renewalDate) : "its next billing date"
              }.`,
            },
          ]}
          noText="Keep cancellation"
          yesText="Reactivate subscription"
          onNo={onClose}
          onYes={() => {
            onClose();
            reactivateSubscription();
          }}
        />
      ),
      closeOnClickOutside: false,
      closeOnEscape: true,
    });
  };

  const showUpgradeReactivationConfirmation = (plan: IPublicPlan) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <ConfirmModal
          title="Reactivate before upgrading"
          description={[
            {
              text: `Your existing ${currentPackage?.name || "Cemboo"} subscription is scheduled to end. Reactivate it to continue with the upgrade to ${plan.package.name}.`,
            },
          ]}
          noText="Keep cancellation"
          yesText="Reactivate & continue"
          onNo={onClose}
          onYes={() => {
            onClose();
            reactivateSubscription(() => showUpgradeConfirmation(plan));
          }}
        />
      ),
      closeOnClickOutside: false,
      closeOnEscape: true,
    });
  };

  React.useEffect(() => {
    refresh()
      .catch((error) => toast.error(errorMessage(error)))
      .finally(() => setLoading(false));
  }, [refresh]);

  const handleUpgrade = async (plan: IPublicPlan, preview: ISubscriptionUpgradePreview) => {
    setUpgradingPackageId(plan.package.id);
    try {
      const result = await paymentService.upgradeMySubscription(
        plan.package.id,
        preview.prorationDate
      );
      if (result.clientSecret && result.paymentStatus === "ACTION_REQUIRED") {
        const stripe = await stripePromise;
        if (!stripe) throw new Error("Stripe could not be loaded.");
        const confirmation = await stripe.confirmPayment({
          clientSecret: result.clientSecret,
          redirect: "if_required",
        });
        if (confirmation.error) throw confirmation.error;
      }

      const refreshedSubscription = await refresh();
      if (refreshedSubscription.package?.id === plan.package.id) {
        toast.success(`Your plan has been upgraded to ${plan.package.name}.`);
      } else if (result.paymentStatus === "PAYMENT_FAILED") {
        toast.error("The saved card was declined. Your current plan has not changed.");
      } else {
        toast.info("Payment is still processing. Your plan will change after it succeeds.");
      }
    } catch (error) {
      toast.error(errorMessage(error));
      await refresh().catch(() => undefined);
    } finally {
      setUpgradingPackageId(undefined);
    }
  };

  const showUpgradeConfirmation = async (plan: IPublicPlan) => {
    setUpgradingPackageId(plan.package.id);
    try {
      const preview = await paymentService.previewMySubscriptionUpgrade(plan.package.id);
      setUpgradingPackageId(undefined);
      confirmAlert({
        customUI: ({ onClose }) => (
          <ConfirmModal
            title={`Upgrade to ${plan.package.name}?`}
            description={[
              {
                text: "Review the prorated charge before changing your Cemboo subscription.",
              },
            ]}
            customContent={
              <Styled.UpgradeSummary>
                <Styled.SummaryPlanChange>
                  <span>{currentPackage?.name}</span>
                  <i className="fas fa-arrow-right" />
                  <strong>{plan.package.name}</strong>
                </Styled.SummaryPlanChange>
                <Styled.SummaryRow>
                  <span>Charge for remaining time</span>
                  <span>{formatMoney(preview.remainingTimeCharge, preview.currency)}</span>
                </Styled.SummaryRow>
                <Styled.SummaryRow>
                  <span>Credit for unused time</span>
                  <Styled.Credit>
                    -{formatMoney(preview.unusedTimeCredit, preview.currency)}
                  </Styled.Credit>
                </Styled.SummaryRow>
                <Styled.SummaryTotal>
                  <span>Estimated charge today</span>
                  <strong>{formatMoney(preview.amountDue, preview.currency)}</strong>
                </Styled.SummaryTotal>
                <Styled.SummaryRow>
                  <span>New monthly price</span>
                  <span>{formatMoney(preview.newMonthlyAmount, preview.currency)}/month</span>
                </Styled.SummaryRow>
                <Styled.SummaryNote>
                  Your next billing date is{" "}
                  {new Date(preview.nextBillingAt * 1000).toLocaleDateString()}. Your current plan
                  stays active if payment fails. This amount is an estimate; taxes, discounts,
                  credits, and final Stripe adjustments may make the actual charge slightly
                  different.
                </Styled.SummaryNote>
              </Styled.UpgradeSummary>
            }
            noText="Keep current plan"
            yesText={`Pay ${formatMoney(preview.amountDue, preview.currency)} & upgrade`}
            onNo={onClose}
            onYes={() => {
              onClose();
              handleUpgrade(plan, preview);
            }}
          />
        ),
        closeOnClickOutside: false,
        closeOnEscape: true,
      });
    } catch (error) {
      setUpgradingPackageId(undefined);
      toast.error(errorMessage(error));
    }
  };

  if (loading) return <Styled.Loading>Loading your plan...</Styled.Loading>;

  const hasPendingManualBilling = Boolean(pendingManualBilling);
  const paymentIssue =
    ["PAYMENT_FAILED", "ACTION_REQUIRED"].includes(
      state.currentUser?.subscriptionInfo?.paymentStatus || ""
    ) &&
    billingMode !== "MANUAL" &&
    !hasPendingManualBilling;
  const actionRequired = state.currentUser?.subscriptionInfo?.paymentStatus === "ACTION_REQUIRED";
  const expiredCard = isExpiredCard(paymentMethod);
  const currentMonthlyPrice = Number(currentPackage?.monthlyPrice || 0);
  const isManual = billingMode === "MANUAL";
  const pendingManualEffectiveAt = pendingManualBilling?.effectiveAt
    ? Math.floor(new Date(pendingManualBilling.effectiveAt).getTime() / 1000)
    : null;
  const manualEndAt = manualBilling?.accessEndsAt
    ? Math.floor(new Date(manualBilling.accessEndsAt).getTime() / 1000)
    : null;
  const manualGraceAt = manualBilling?.graceEndsAt
    ? Math.floor(new Date(manualBilling.graceEndsAt).getTime() / 1000)
    : null;

  return (
    <Styled.Container>
      <Styled.Title>My Plan</Styled.Title>
      <Styled.Subtitle>
        Review your current Cemboo subscription and available public upgrades.
      </Styled.Subtitle>

      <Styled.OverviewGrid>
        <Styled.CurrentPlan>
          {currentPackage ? (
            <>
              <Styled.PlanHeader>
                <div>
                  <Styled.Eyebrow>Current plan</Styled.Eyebrow>
                  <Styled.PlanName>{currentPackage.name}</Styled.PlanName>
                  <Styled.Description>{currentPackage.description}</Styled.Description>
                </div>
                <Styled.Price>
                  {isManual && manualBilling
                    ? formatMoney(manualBilling.agreedAmount, manualBilling.currency)
                    : `$${Number(currentPackage.monthlyPrice).toFixed(2)}`}{" "}
                  <span>
                    {isManual
                      ? manualBilling?.agreementType === "MONTHLY_RATE"
                        ? "/ negotiated month"
                        : "manual agreement"
                      : "/ month"}
                  </span>
                </Styled.Price>
              </Styled.PlanHeader>
              <Styled.Features>
                {currentPackage.features?.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </Styled.Features>
            </>
          ) : (
            <Styled.Description>
              No active Cemboo plan was found for this account.
            </Styled.Description>
          )}
          {paymentIssue && (
            <Styled.PaymentWarning>
              {actionRequired
                ? "Your bank needs you to authenticate the latest payment. Your current plan remains in place until it completes."
                : "Your latest subscription payment did not go through. Update your card to retry the outstanding invoice."}
            </Styled.PaymentWarning>
          )}
          {pendingManualBilling && (
            <Styled.ScheduledPlanNotice>
              Your manual payment is recorded. The{" "}
              <strong>{pendingManualBilling.packageName}</strong> plan will begin on{" "}
              {pendingManualEffectiveAt
                ? formatBillingDate(pendingManualEffectiveAt)
                : "the scheduled effective date"}
              , and this Stripe subscription will not renew.
            </Styled.ScheduledPlanNotice>
          )}
          {isManual && manualAccessStatus === "GRACE" && (
            <Styled.PaymentWarning>
              Your paid manual access has ended. You are in the seven-day grace period through{" "}
              {manualGraceAt ? formatBillingDate(manualGraceAt) : "the configured grace date"}.
            </Styled.PaymentWarning>
          )}
          {isManual && manualAccessStatus === "EXPIRED" && (
            <Styled.PaymentWarning>
              Your manual agreement and grace period have ended. Contact Cemboo to renew access.
            </Styled.PaymentWarning>
          )}
          {currentPackage && (
            <Styled.PlanFooter>
              <Styled.NextBillingDate>
                <span>
                  {isManual
                    ? "Manual access paid through"
                    : cancelAtPeriodEnd
                      ? "Plan access ends"
                      : "Next billing date"}
                </span>
                <strong>
                  {isManual && manualEndAt
                    ? formatBillingDate(manualEndAt)
                    : nextBillingAt || cancelAt
                      ? formatBillingDate(cancelAt || nextBillingAt || 0)
                      : "Not available"}
                </strong>
                {isManual && manualAccessStatus === "GRACE" && manualGraceAt ? (
                  <small>Seven-day grace period ends {formatBillingDate(manualGraceAt)}.</small>
                ) : cancelAtPeriodEnd ? (
                  <small>No additional renewal charge will be made.</small>
                ) : null}
              </Styled.NextBillingDate>
              <Styled.PlanActions>
                <Styled.PlanActionButton type="button" onClick={() => setShowInvoices(true)}>
                  Invoices
                </Styled.PlanActionButton>
                {!isManual && hasPendingManualBilling ? (
                  <Styled.PlanActionButton type="button" disabled>
                    Manual plan scheduled
                  </Styled.PlanActionButton>
                ) : !isManual ? (
                  <Styled.PlanActionButton
                    type="button"
                    $danger={!cancelAtPeriodEnd}
                    disabled={!nextBillingAt || cancelling}
                    onClick={
                      cancelAtPeriodEnd
                        ? showReactivationConfirmation
                        : showCancellationConfirmation
                    }
                  >
                    {cancelling
                      ? cancelAtPeriodEnd
                        ? "Reactivating..."
                        : "Scheduling..."
                      : cancelAtPeriodEnd
                        ? "Reactivate subscription"
                        : "Cancel subscription"}
                  </Styled.PlanActionButton>
                ) : null}
              </Styled.PlanActions>
            </Styled.PlanFooter>
          )}
        </Styled.CurrentPlan>

        <Styled.PaymentMethodCard $issue={!isManual && (paymentIssue || expiredCard)}>
          {isManual ? (
            <>
              <Styled.Eyebrow>Manual billing</Styled.Eyebrow>
              <Styled.PlanName>No card required</Styled.PlanName>
              <Styled.ManualBillingDetails>
                <div>
                  <span>Latest payment</span>
                  <strong>
                    {manualBilling
                      ? formatMoney(manualBilling.amountPaid, manualBilling.currency)
                      : "Recorded offline"}
                  </strong>
                </div>
                <div>
                  <span>Payment method</span>
                  <strong>{manualBilling?.paymentMethod?.replaceAll("_", " ") || "Manual"}</strong>
                </div>
                <div>
                  <span>Access status</span>
                  <strong>{manualAccessStatus}</strong>
                </div>
              </Styled.ManualBillingDetails>
              <Styled.BillingAddress>
                To enjoy uninterrupted service beyond your manual agreement, contact support or add
                a card on file. Adding a card will not charge it or switch your billing
                automatically.
              </Styled.BillingAddress>
              {paymentMethod && (
                <Styled.PaymentMethodNote>
                  {paymentMethod.card
                    ? `${paymentMethod.card.brand} ending in ${paymentMethod.card.last4} is saved for a future switch to automatic billing.`
                    : `${formatPaymentMethodType(paymentMethod.type)} is saved for a future switch to automatic billing.`}
                </Styled.PaymentMethodNote>
              )}
              <Styled.BillingActions>
                <Styled.BillingButton
                  type="button"
                  $secondary
                  onClick={() => {
                    window.location.href = "/contact";
                  }}
                >
                  Contact support
                </Styled.BillingButton>
                <Styled.BillingButton
                  type="button"
                  onClick={openPaymentMethodUpdate}
                  disabled={openingPaymentUpdate}
                >
                  {openingPaymentUpdate
                    ? "Opening..."
                    : paymentMethod
                      ? "Update card on file"
                      : "Add card for later"}
                </Styled.BillingButton>
              </Styled.BillingActions>
            </>
          ) : (
            <>
              <Styled.Eyebrow>
                {paymentIssue ? "Payment needs attention" : "Payment method"}
              </Styled.Eyebrow>
              <Styled.PlanName>Billing details</Styled.PlanName>
              {paymentMethod ? (
                <>
                  <Styled.BillingCardVisual>
                    <Styled.CardBrandRow>
                      <span>
                        {paymentMethod.card?.brand ||
                          paymentMethod.bankAccount?.bankName ||
                          formatPaymentMethodType(paymentMethod.type)}
                      </span>
                      <span>
                        {paymentMethod.card?.funding ||
                          paymentMethod.bankAccount?.accountType ||
                          "Stripe"}
                      </span>
                    </Styled.CardBrandRow>
                    <Styled.CardNumber>
                      {paymentMethod.card || paymentMethod.bankAccount?.last4
                        ? `**** **** **** ${
                            paymentMethod.card?.last4 || paymentMethod.bankAccount?.last4
                          }`
                        : formatPaymentMethodType(paymentMethod.type)}
                    </Styled.CardNumber>
                    <Styled.CardMeta>
                      <span>{paymentMethod.billingDetails?.name || "Cardholder"}</span>
                      <span>
                        {paymentMethod.card
                          ? `${String(paymentMethod.card.expMonth).padStart(2, "0")}/${String(
                              paymentMethod.card.expYear
                            ).slice(-2)}`
                          : formatPaymentMethodType(paymentMethod.type)}
                      </span>
                    </Styled.CardMeta>
                  </Styled.BillingCardVisual>
                  <Styled.BillingAddress>
                    {expiredCard && "This card is expired. "}
                    {formatBillingAddress(paymentMethod)}
                  </Styled.BillingAddress>
                  {paymentMethodSource === "ATTACHED" && (
                    <Styled.PaymentMethodNote>
                      Stripe has no default payment method selected. Showing the most recently
                      attached method{paymentMethodCount > 1 ? ` of ${paymentMethodCount}` : ""}.
                    </Styled.PaymentMethodNote>
                  )}
                  {paymentMethodSource === "LAST_USED" && (
                    <Styled.PaymentMethodNote>
                      Showing the payment method used for the latest invoice.
                    </Styled.PaymentMethodNote>
                  )}
                </>
              ) : (
                <Styled.BillingAddress>
                  No payment method is attached to this Stripe customer. Add one before upgrading or
                  resolving an outstanding invoice.
                </Styled.BillingAddress>
              )}
              {!hasPendingManualBilling && (
                <Styled.BillingActions>
                  {actionRequired && paymentClientSecret && (
                    <Styled.BillingButton
                      type="button"
                      onClick={completePaymentAuthentication}
                      disabled={authenticatingPayment}
                    >
                      {authenticatingPayment ? "Authenticating..." : "Complete authentication"}
                    </Styled.BillingButton>
                  )}
                  <Styled.BillingButton
                    type="button"
                    $secondary={actionRequired && Boolean(paymentClientSecret)}
                    onClick={openPaymentMethodUpdate}
                    disabled={openingPaymentUpdate}
                  >
                    {openingPaymentUpdate
                      ? "Opening..."
                      : paymentMethod
                        ? paymentIssue || expiredCard
                          ? "Fix payment method"
                          : "Update card or address"
                        : "Add payment method"}
                  </Styled.BillingButton>
                </Styled.BillingActions>
              )}
            </>
          )}
        </Styled.PaymentMethodCard>
      </Styled.OverviewGrid>

      <Styled.SectionTitle>Available plans</Styled.SectionTitle>
      <Styled.Description>
        {isManual
          ? "Your manual agreement will not charge a card. Contact Cemboo to change plans or switch to automatic billing."
          : hasPendingManualBilling
            ? `Your paid ${pendingManualBilling?.packageName || "manual"} plan is scheduled. Stripe will not renew the current subscription.`
            : "Upgrades are prorated and charged immediately using your saved payment method."}
      </Styled.Description>
      <Styled.Cards>
        {plans.map((plan) => {
          const isCurrent = plan.package.id === currentPackage?.id;
          const isHigherPrice = Number(plan.package.monthlyPrice) > currentMonthlyPrice;
          const isContact = plan.action === "CONTACT";
          const actionDisabled =
            isCurrent ||
            (!isContact && (!currentPackage || !isHigherPrice)) ||
            Boolean(upgradingPackageId) ||
            (!isContact && hasPendingManualBilling) ||
            (!isContact && paymentIssue && !cancelAtPeriodEnd);
          const actionLabel = isCurrent
            ? "Current plan"
            : isManual
              ? "Contact us to change plan"
              : hasPendingManualBilling && !isContact
                ? "Manual plan scheduled"
                : isContact
                  ? "Contact Us"
                  : !isHigherPrice
                    ? "Upgrade only"
                    : cancelAtPeriodEnd
                      ? "Plan ending"
                      : upgradingPackageId === plan.package.id
                        ? "Processing..."
                        : `Upgrade to ${plan.package.name}`;

          return (
            <PublicPlanCard
              key={plan.package.id}
              plan={plan}
              actionLabel={actionLabel}
              actionDisabled={actionDisabled}
              onAction={
                isContact || isManual
                  ? () => {
                      window.location.href = `/contact?packageId=${encodeURIComponent(plan.package.id)}&plan=${encodeURIComponent(plan.package.name)}`;
                    }
                  : cancelAtPeriodEnd
                    ? () => showUpgradeReactivationConfirmation(plan)
                    : () => showUpgradeConfirmation(plan)
              }
            />
          );
        })}
      </Styled.Cards>

      <Styled.PaymentModal
        show={showInvoices}
        onHide={() => setShowInvoices(false)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <Modal.Title>Invoices</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Styled.ModalNote>Your 25 most recent Stripe invoices and receipts.</Styled.ModalNote>
          <Styled.InvoiceTableWrap>
            {invoices.length ? (
              <Styled.InvoiceTable>
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Documents</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>
                        <Styled.InvoiceNumber>{invoice.number || invoice.id}</Styled.InvoiceNumber>
                      </td>
                      <td>{new Date(invoice.created * 1000).toLocaleDateString()}</td>
                      <td>
                        <Styled.InvoiceStatus $status={invoice.status}>
                          {invoice.status || "unknown"}
                        </Styled.InvoiceStatus>
                      </td>
                      <td>{formatMoney(invoice.total, invoice.currency)}</td>
                      <td>
                        <Styled.InvoiceActions>
                          {invoice.hostedInvoiceUrl && (
                            <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noreferrer">
                              View
                            </a>
                          )}
                          {invoice.invoicePdf && (
                            <a href={invoice.invoicePdf} target="_blank" rel="noreferrer">
                              PDF
                            </a>
                          )}
                          {!invoice.hostedInvoiceUrl && !invoice.invoicePdf && <span>-</span>}
                        </Styled.InvoiceActions>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Styled.InvoiceTable>
            ) : (
              <Styled.EmptyInvoices>
                No past invoices were found for this account.
              </Styled.EmptyInvoices>
            )}
          </Styled.InvoiceTableWrap>
        </Modal.Body>
      </Styled.PaymentModal>

      <Styled.PaymentModal
        show={Boolean(setupClientSecret)}
        onHide={() => setSetupClientSecret(undefined)}
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Update card and billing address</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {setupClientSecret && (
            <Elements
              key={setupClientSecret}
              stripe={stripePromise}
              options={{ clientSecret: setupClientSecret, appearance: stripeAppearance }}
            >
              <PaymentMethodForm
                onCancel={() => setSetupClientSecret(undefined)}
                onComplete={finishPaymentMethodUpdate}
                manualMode={isManual}
                getErrorMessage={errorMessage}
              />
            </Elements>
          )}
        </Modal.Body>
      </Styled.PaymentModal>
    </Styled.Container>
  );
};
